/* bysize-v4-print-big.js — PRINT FILE for By Size designs over imgix's render cap (Owen 2026-10-15, option 1).
 *
 * A single design whose trimmed art exceeds imgix's per-side render cap cannot ship the usual
 * imgix-trimmed production render (it would arrive downscaled), so the engine switches such a
 * file to the RAW S3 original (pipeline.js applyRenderCap → file.bigFile, order [BOTH_S3]). The
 * raw file is the whole CANVAS, margins included — "what a customer sees as 22x24 prints as 22x45".
 * This module gives Art the ART instead: at checkout every (file, size) pair of a big raster is
 * rendered by the gang render Lambda (ninja-labs-gang-render, `trim: true, threshold: false`,
 * `pad: false`) into an exact-size 300-dpi PNG, trimmed to the art, alpha as uploaded. The order
 * line's Upload becomes that PNG; the customer's original stays in `_Original Image`; `_Render Job`
 * lets the Lambda's self-healing sweeper re-run a job that died. Checkout waits for ACCEPTANCE
 * only (~1 s): the output name is chosen here, so the line carries the URL before the render ends
 * (same contract as the gang card's dirty-sheet render, snippets/guv2-card.liquid).
 *
 * One shared module for BOTH engines (assets/bysize-v4.js and the live DTF fork
 * assets/nt-bgr-common.js): it reads window.__bsv4Bridge and hooks the two seams the engines
 * already expose — window.__bsv4PrepareBig (cart.js, before the add) and window.__bsv4ItemProps
 * (cart-payload.js buildItem). Vectors are not rendered here: they ship raw + _width/_height as
 * every by-size vector always has (the RIP scales them).
 */
(function () {
  'use strict';
  if (window.__bsv4PrintBig) return;
  window.__bsv4PrintBig = true;

  var API = 'https://uszaouez69.execute-api.us-east-1.amazonaws.com';
  var S3 = 'https://ninja-services-production-ninjauploadss3bucket-zks2mguobhe4.s3.amazonaws.com/';
  var START_MS = 30000; // the Lambda validates + queues the job and answers 202 well inside this
  var VECTOR = /^(ai|eps|pdf|svg)$/i;
  var jobs = {}; // "<fileId>|<rowId>" -> { w, h, name, jobId, accepted }

  function bridge() { return window.__bsv4Bridge || null; }
  function eligible(f) { return !!(f && f.bigFile && !f.sheet && !VECTOR.test(f.ext || '')); }
  function keyOf(f) { return String(f.s3Url || f.imgixUrl || '').split('?')[0].split('#')[0].split('/').pop() || ''; }
  function fmtIn(v) { var s = (Math.round(Number(v) * 100) / 100).toFixed(2); return s.replace(/\.?0+$/, '') || '0'; }
  function stemOf(f, key) {
    return String(f.name || key).replace(/\.[^.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'design';
  }
  function post(path, body) {
    var ctl = new AbortController(); var t = setTimeout(function () { ctl.abort(); }, START_MS);
    return fetch(API + path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), signal: ctl.signal })
      .then(function (r) { return r.json(); })
      .finally(function () { clearTimeout(t); });
  }

  // One render per (file, row): a second size of the same design is its own print file.
  function ensure(f, row) {
    var k = f.id + '|' + row.id;
    var j = jobs[k];
    if (j && Math.abs(j.w - row.w) < 0.01 && Math.abs(j.h - row.h) < 0.01) return j;
    var key = keyOf(f);
    if (!key || !(row.w > 0) || !(row.h > 0)) return { accepted: Promise.resolve(false) };
    var name = stemOf(f, key) + '-print-' + fmtIn(row.w) + 'x' + fmtIn(row.h) + '____image_' + Date.now() + '_' + Math.floor(Math.random() * 1000) + '.png';
    var body = { key: key, ext: String(f.ext || 'png').toLowerCase(), inW: +f.inW, inH: +f.inH, action: 'width', width: +row.w, pad: false, trim: true, threshold: false, name: name };
    j = { w: +row.w, h: +row.h, name: name, jobId: null, accepted: null };
    j.accepted = post('/render', body).then(function (r) {
      if (!r || !r.ok || !r.jobId) throw new Error('render-start');
      j.jobId = r.jobId; // URL known from here on: the line carries it while the Lambda renders
      return true;
    }).catch(function (e) { console.warn('[bysize print-big] render not accepted', e && e.message); if (jobs[k] === j) delete jobs[k]; return false; });
    jobs[k] = j;
    return j;
  }

  // cart.js (both engines) awaits this before the add when any file is a big raster.
  window.__bsv4PrepareBig = function () {
    var B = bridge(); var st = B && B.getState ? B.getState() : null; var list = [];
    ((st && st.files) || []).forEach(function (f) {
      if (!eligible(f)) return;
      (f.rows || []).forEach(function (row) { list.push(ensure(f, row)); });
    });
    if (!list.length) return Promise.resolve(true);
    return Promise.all(list.map(function (j) { return j.accepted; })).then(function (rs) { return !rs.some(function (r) { return !r; }); }, function () { return false; });
  };

  // cart-payload.js buildItem merges this over the line's properties. Chained: whatever another
  // module (the gang card on gang pages) defined first still runs.
  var prev = window.__bsv4ItemProps;
  window.__bsv4ItemProps = function (file, row) {
    var base = (typeof prev === 'function' && prev(file, row)) || {};
    if (!eligible(file)) return base;
    var j = jobs[file.id + '|' + row.id];
    if (!j || !j.jobId) return base;
    var p = {}; for (var k in base) if (Object.prototype.hasOwnProperty.call(base, k)) p[k] = base[k];
    p['Upload (Vector Files Preferred)'] = S3 + j.name;
    p['_Render Job'] = j.jobId;
    p['_Art Note'] = 'Oversized design: print-ready PNG attached at ' + fmtIn(row.w) + ' x ' + fmtIn(row.h) + ' in, 300 dpi, trimmed to the art, alpha as uploaded. The original file is in _Original Image.';
    return p;
  };
})();
